import React from 'react';
import { StepViewModel } from '../../models/step';
import { InputType } from '../../enums/input-type.enum';
import { ColorPickerViewRender } from '../../views/ColorPickerViewRender';
import { Option } from '../../models/option';
import { StepValues } from '../../models/step-values';
import { ProductHandle } from '../../../enums/ProductHandle.enum';
import { FinalSizeViewRender } from '../../views/FinalSizeViewRender';
import { getPossibleHipSizeFromWaist } from '../../utils/waist-hip-helper';

export const EverydayUnderwearSteps: StepViewModel[] = [
  {
    key: 'please-measure-your-waist',
    text: 'Please measure your waist as shown below:',
    subText:
      'Waist measurements should be taken at the natural waist, the smallest point of the torso. It is usually a few inches below your bust, and a couple inches above your belly button.',
    image: 'measure-waist.svg',
    options: [
      {
        text: 'CONTINUE',
        inputType: InputType.CTAButton,
        nextStepKey: 'waist-measurement',
      },
    ],
  },
  {
    key: 'waist-measurement',
    text: 'What is your waist measurement?',
    options: [
      {
        imperialText: '23" or lower',
        metricText: '58 cm or lower',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '24"',
        metricText: '61 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '25"',
        metricText: '64 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '26"',
        metricText: '66 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '27"',
        metricText: '69 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '28"',
        metricText: '71 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '29"',
        metricText: '74 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '30"',
        metricText: '76 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '31"',
        metricText: '79 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '32"',
        metricText: '81 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '33"',
        metricText: '84 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '34"',
        metricText: '86 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '35"',
        metricText: '89 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '36"',
        metricText: '91 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '37"',
        metricText: '94 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '38"',
        metricText: '97 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '39"',
        metricText: '99 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '40"',
        metricText: '102 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '41"',
        metricText: '104 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '42"',
        metricText: '107 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '43"',
        metricText: '109 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '44"',
        metricText: '112 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '45"',
        metricText: '114 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '46"',
        metricText: '117 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '47"',
        metricText: '119 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '48"',
        metricText: '122 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '49"',
        metricText: '124 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '50"',
        metricText: '127 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '51"',
        metricText: '130 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '52"',
        metricText: '132 cm',
        nextStepKey: 'hip-instructions',
      },
      {
        imperialText: '53" or above',
        metricText: '135 cm',
        nextStepKey: 'hip-instructions',
      },
    ],
  },
  {
    key: 'hip-instructions',
    text: 'Got it! Next, let’s take your hip measurement:',
    subText: 'Hip measurements should be taken at the fullest point around your hips or bum, the placement can vary from body to body.',
    image: 'measure-hip.svg',
    options: [
      {
        text: 'CONTINUE',
        inputType: InputType.CTAButton,
        nextStepKey: 'hip-measurement',
      },
    ],
  },
  {
    key: 'hip-measurement',
    text: 'What is your hip measurement?',
    filter: (option: Option, stepValues: StepValues, productHandle: ProductHandle): boolean => {
      // Todo: Could potentially cache results to speed this up
      const waistSizeResponse = stepValues.get('waist-measurement');
      if (!waistSizeResponse || !waistSizeResponse.value || !option || !option.imperialText) {
        return false;
      }
      const waistSize = parseInt(waistSizeResponse.value, 10);
      const potentialSizes = getPossibleHipSizeFromWaist(waistSize);
      if (!potentialSizes) {
        return false;
      }

      const optionSize = parseInt(option.imperialText, 10);
      if (optionSize >= potentialSizes.lowerHipBound && optionSize <= potentialSizes.upperHipBound) {
        return true;
      }

      return false;
    },
    options: [
      {
        imperialText: '27" or lower',
        metricText: '69 cm or lower',
        nextStepKey: 'pants',
      },
      {
        imperialText: '28"',
        metricText: '71 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '29"',
        metricText: '74 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '30"',
        metricText: '76 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '31"',
        metricText: '79 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '32"',
        metricText: '81 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '33"',
        metricText: '84 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '34"',
        metricText: '86 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '35"',
        metricText: '89 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '36"',
        metricText: '91 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '37"',
        metricText: '94 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '38"',
        metricText: '97 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '39"',
        metricText: '99 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '40"',
        metricText: '102 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '41"',
        metricText: '104 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '42"',
        metricText: '107 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '43"',
        metricText: '109 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '44"',
        metricText: '112 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '45"',
        metricText: '114 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '46"',
        metricText: '117 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '47"',
        metricText: '119 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '48"',
        metricText: '122 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '49"',
        metricText: '124 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '50"',
        metricText: '127 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '51"',
        metricText: '130 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '52"',
        metricText: '132 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '53"',
        metricText: '135 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '54"',
        metricText: '137 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '55"',
        metricText: '140 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '56"',
        metricText: '142 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '57"',
        metricText: '145 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '58"',
        metricText: '147 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '59"',
        metricText: '150 cm',
        nextStepKey: 'pants',
      },
      {
        imperialText: '60" or above',
        metricText: '152 cm or above',
        nextStepKey: 'not-ready',
      },
    ],
  },
  {
    key: 'pants',
    text: 'What size pants do you wear?',
    options: [
      {
        text: '2',
        nextStepKey: 'results',
      },
      {
        text: '4',
        nextStepKey: 'results',
      },
      {
        text: '6',
        nextStepKey: 'results',
      },
      {
        text: '8',
        nextStepKey: 'results',
      },
      {
        text: '10',
        nextStepKey: 'results',
      },
      {
        text: '12',
        nextStepKey: 'results',
      },
      {
        text: '14',
        nextStepKey: 'results',
      },
      {
        text: '16',
        nextStepKey: 'results',
      },
      {
        text: '18',
        nextStepKey: 'results',
      },
      {
        text: '20',
        nextStepKey: 'results',
      },
      {
        text: '22',
        nextStepKey: 'results',
      },
      {
        text: '24',
        nextStepKey: 'results',
      },
      {
        text: '26 or above',
        nextStepKey: 'not-ready',
      },
    ],
  },
  {
    key: 'results',
    text: '[engine]',
    progress: 95,
    options: [
      {
        text: '',
        nextStepKey: 'pick-a-color',
      },
    ],
    render: (props, onChange) => {
      return <FinalSizeViewRender {...props} nextStepKey="pick-a-color" onChange={onChange} />;
    },
  },
  {
    key: 'pick-a-color',
    text: 'pick-a-color',
    progress: 100,
    options: [],
    render: (props, onChange) => {
      return <ColorPickerViewRender {...props} onChange={onChange} />;
    },
  },
  {
    key: 'not-ready',
    text: `Hey love,

Our design team is still working on the size 4X, and it's not available yet.

If you'd like to be notified as soon as our expanded sizes launch, please leave your email below.

Alternatively, you can order in size 3X - if that's not the right size, we have an excellent return policy :)`,
    progress: 99,
    options: [
      {
        inputType: InputType.Email,
        nextStepKey: 'results',
        klaviyoKey: 'klaviyo4x5xId',
      },
      {
        inputType: InputType.SecondaryCTAButton,
        nextStepKey: 'pick-a-color',
        text: 'ORDER IN SIZE 3X',
      },
    ],
  },
];
